<template>
    <list-container fixed>
        <template #header>
            <list-cell-header :width="25">
                <template #title>
                    Vestiging
                </template>
            </list-cell-header>
            <list-cell-header :width="35">
                <template #title>
                    Adres
                </template>
            </list-cell-header>
            <list-cell-header v-if="hasWinckBranding()" :width="30">
                <template #title>
                    {{ $t('domain.brand', 2) }}
                </template>
            </list-cell-header>
            <list-cell-header :width="10" />
        </template>
        <template #content>
            <list-row
                v-for="(team, index) in sortedTeams"
                :key="team.id.value"
                :background-color="index % 2 ? 'gray-100' : 'white'"
                @mouseenter="setCurrentTeamId(team.id)"
                @focusin="setCurrentTeamId(team.id)"
                @mouseleave="resetCurrentTeamId()"
                @focusout="resetCurrentTeamId()"
            >
                <list-cell-text>
                    <template #content>
                        {{ team.name }}
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        <template v-if="team.address && team.address?.fullAddress !== '-'">
                            {{ team.address.fullAddress }}
                        </template>
                        <template v-else>
                            <chip-informative :color="'gray-500'">
                                Adres onbekend
                            </chip-informative>
                        </template>
                    </template>
                </list-cell-text>
                <list-cell-text v-if="hasWinckBranding()">
                    <template #content>
                        <template v-if="team.brand">
                            {{ team.brand?.name ?? t(`noBrandChosen`) }}
                        </template>
                        <template v-else>
                            <chip-informative :color="'gray-500'">
                                {{ t(`noBrandChosen`) }}
                            </chip-informative>
                        </template>
                    </template>
                </list-cell-text>
                <list-cell>
                    <flex-container
                        v-show="team.id.equals(currentTeamId)"
                        height="full"
                        align-content="center"
                        justify-content="end"
                        style="padding-right: 10px"
                    >
                        <collapse-box-decorator
                            horizontal-position="right"
                            trigger="hover"
                        >
                            <template #default>
                                <svg-icon
                                    :icon="MoreVertIcon"
                                    :size="IconSize.lg"
                                    :color="'primary'"
                                />
                            </template>
                            <template #content>
                                <collapse-option-container
                                    @mouseenter="setCurrentTeamId(team.id)"
                                    @focusin="setCurrentTeamId(team.id)"
                                    @mouseleave="resetCurrentTeamId()"
                                    @focusout="resetCurrentTeamId()"
                                >
                                    <collapse-option @click="$router.push({ name: 'team-edit', params: { teamId: team.id.value } })">
                                        Bewerken
                                    </collapse-option>
                                    <collapse-option @click="$router.push({ name: 'team-delete', params: { teamId: team.id.value } })">
                                        <text-decorator :color="'red-500'">
                                            Verwijderen
                                        </text-decorator>
                                    </collapse-option>
                                </collapse-option-container>
                            </template>
                        </collapse-box-decorator>
                    </flex-container>
                </list-cell>
            </list-row>
        </template>
    </list-container>
</template>

<script setup lang="ts">
    import {
        ChipInformative,
        CollapseBoxDecorator,
        CollapseOption,
        CollapseOptionContainer,
        FlexContainer,
        IconSize,
        ListCell,
        ListCellHeader,
        ListCellText,
        ListContainer,
        ListRow,
        SvgIcon,
    } from '@keyvue/one-ui';
    import MoreVertIcon from '@keyvue/one-material-icons/icons/more_vert.svg?component';
    import type { Team, TeamId } from '@keyvue/one-api-client';
    import { computed, ref } from 'vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import { useI18n } from 'vue-i18n';
    import messages from './TeamList.messages.json';

    const { t } = useI18n({ messages });

    export interface Props {
        teams: Team[]
    }

    const props = defineProps<Props>();

    const currentTeamId = ref<null | TeamId>(null);
    const currentThrottle = ref<undefined | number>(undefined);

    const { hasWinckBranding } = useAppBranding();

    function setCurrentTeamId(id: TeamId) {
        clearTimeout(currentThrottle.value);
        currentThrottle.value = undefined;
        currentTeamId.value = id;
    }

    function resetCurrentTeamId() {
        currentThrottle.value = setTimeout(() => {
            currentTeamId.value = null;
        }, 50) as unknown as number;
    }

    const sortedTeams = computed(() => ([...props.teams].sort((a, b) => a.name.localeCompare(b.name))));
</script>
